/**
 * ConfigDesktopPage
 *
 * Admin page to configure the Desktop Bridge Pub/Sub routing topics.
 * Reads/writes organisation/{org_id}/config/desktop in Firestore via
 * the /admin/config/desktop endpoints.
 *
 * Route: /settings/config/desktop  (admin-only)
 */

import { useCallback, useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";
import useFeedback from "../../hooks/useFeedback";
import SettingsPageShell from "../../components/Settings/SettingsPageShell";
import { PrimaryButton } from "../../components/Button/Buttons";
import Input from "../../components/Inputs/Input";
import Loader from "../../components/Loader/Loader";
import { getServiceConfig, updateServiceConfig } from "../../api/adminConfig";
import { AuthError } from "../../api";
import UIStore from "../../store/uiStore";
import { isDirty } from "../../utils/isDirty";

const EMPTY_FORM = {
  tool_calls_topic:   "",
  tool_results_topic: "",
  pip_index_url:      "",
};

const ConfigDesktopPage = () => {
  const { showFeedback } = useFeedback();
  const orgAvailable = UIStore((s) => s.orgAvailable);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [form, setForm]       = useState(EMPTY_FORM);
  const [originalForm, setOriginalForm] = useState(EMPTY_FORM);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getServiceConfig("desktop");
      if (data.configured) {
        const loaded = {
          tool_calls_topic:   data.tool_calls_topic   ?? "",
          tool_results_topic: data.tool_results_topic ?? "",
          pip_index_url:      data.pip_index_url      ?? "",
        };
        setForm(loaded);
        setOriginalForm(loaded);
      } else {
        setForm(EMPTY_FORM);
        setOriginalForm(EMPTY_FORM);
      }
    } catch (err) {
      if (!(err instanceof AuthError)) showFeedback("Failed to load Desktop Bridge config.", "error");
    } finally {
      setLoading(false);
    }
  }, [showFeedback]);

  useEffect(() => {
    if (orgAvailable) fetchConfig();
  }, [orgAvailable, fetchConfig]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const normalizedForm = Object.fromEntries(
        Object.entries(form).map(([field, value]) => [field, value.trim()])
      );
      const topicsChanged =
        normalizedForm.tool_calls_topic !== originalForm.tool_calls_topic ||
        normalizedForm.tool_results_topic !== originalForm.tool_results_topic;
      const registryChanged =
        normalizedForm.pip_index_url !== originalForm.pip_index_url;
      await updateServiceConfig("desktop", normalizedForm);
      setForm(normalizedForm);
      setOriginalForm(normalizedForm);
      showFeedback(
        topicsChanged
          ? "Desktop Bridge config saved. Restart both Photon API and Photon WebSocket services to apply topic changes."
          : registryChanged
            ? "Client Package Registry URL saved and active."
            : "Desktop Bridge configuration saved. No runtime changes were required.",
        "success"
      );
    } catch (err) {
      showFeedback(err?.response?.data?.error || "Failed to save Desktop Bridge config.", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <SettingsPageShell
      title="Desktop Bridge"
      description="Configure the Pub/Sub topics used to route tool calls between photon-api and photon-ws. Topics must exist in your GCP project before saving."
    >
      {loading ? (
        <div className="flex justify-center py-16">
          <Loader />
        </div>
      ) : (
        <div className="flex flex-col gap-6 max-w-lg">
          <div className="flex items-start gap-2 bg-fill-hex-fffbeb dark:bg-fill-hex-78350f/20 border border-edge-amber-200 dark:border-edge-amber-700 rounded-lg px-3 py-2.5">
            <AlertTriangle className="w-4 h-4 text-ink-hex-d97706 dark:text-ink-amber-400 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-ink-amber-800 dark:text-ink-amber-300 leading-relaxed">
              Pub/Sub topic changes require restarting both Photon API and Photon WebSocket services. The Client Package Registry URL takes effect immediately.
            </p>
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">
              Tool Calls Topic
            </label>
            <Input
              type="text"
              placeholder="desktop-tool-calls"
              value={form.tool_calls_topic}
              onChange={(e) => setForm((prev) => ({ ...prev, tool_calls_topic: e.target.value }))}
            />
            <p className="text-[11px] text-ink-gray-400 dark:text-ink-gray-500">
              Topic photon-api publishes TOOL_CALL and LIST_TOOLS requests to. photon-ws subscribes to this topic.
            </p>
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">
              Tool Results Topic
            </label>
            <Input
              type="text"
              placeholder="desktop-tool-results"
              value={form.tool_results_topic}
              onChange={(e) => setForm((prev) => ({ ...prev, tool_results_topic: e.target.value }))}
            />
            <p className="text-[11px] text-ink-gray-400 dark:text-ink-gray-500">
              Topic photon-ws publishes TOOL_RESULT, TOOL_LIST, and TOOL_ERROR responses to. photon-api subscribes to this topic.
            </p>
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">
              Client Package Registry URL
            </label>
            <Input
              type="text"
              placeholder="https://us-python.pkg.dev/glb-pa-artifactory/pa-python-releases/simple/"
              value={form.pip_index_url}
              onChange={(e) => setForm((prev) => ({ ...prev, pip_index_url: e.target.value }))}
            />
            <p className="text-[11px] text-ink-gray-400 dark:text-ink-gray-500">
              Python package index URL for <code className="font-mono bg-surface-muted dark:bg-surface-muted px-1 rounded">pip install photon-desktop</code>. Shown to users when connecting a new device. Leave blank to hide the install command.
            </p>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <PrimaryButton onClick={handleSave} disabled={saving || !isDirty(form, originalForm)}>
              {saving ? "Saving…" : "Save"}
            </PrimaryButton>
          </div>
        </div>
      )}
    </SettingsPageShell>
  );
};

export default ConfigDesktopPage;
